import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Link, useNavigate } from "react-router-dom";
import { useAppServices } from "../../../../hook/services";
import { useKnowledgebaseCustomizationInfo } from "../../../../context/knowledgebaseCustomization";
import { useParams } from "react-router-dom";
import Select from "react-select";
import CreateCategory from "../components/Models/Category";
import ThemedLoader from "../../../../components/ThemedLoader";

const HelpCenter = () => {
  const navigate = useNavigate();
  const AppService = useAppServices();
  const { companyId } = useParams();
  const [KnowledgebaseCustomization, languages, Update] =
    useKnowledgebaseCustomizationInfo();
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [languageId, setLanguageId] = useState(
    KnowledgebaseCustomization?.languageId || "6936c576ac8e853aea250bb1"
  );
  const [searchQuery, setSearchQuery] = useState("");

  const getCategories = async () => {
    try {
      const { response } =
        await AppService.knowledgebase_category.get_agency_categories({
          query: `languageId=${languageId}&companyId=${companyId}`,
        });
      setCategories(response?.data || []);
    } catch (error) {
      console.error(error);
      setCategories([]);
    } finally {
    }
  };

  const getArticles = async () => {
    try {
      const { response } =
        await AppService.knowledgebase_article.get_agency_articles({
          query: `languageId=${languageId}&companyId=${companyId}`,
        });
      setArticles(response?.data || []);
    } catch (error) {
      console.error(error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLanguageId(KnowledgebaseCustomization?.languageId || "6936c576ac8e853aea250bb1");
  }, [KnowledgebaseCustomization?.languageId]);

  useEffect(() => {
    setLoading(true);
    getCategories();
    getArticles();
  }, [languageId]);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const matchesQuery = (text) =>
    !normalizedQuery || (text || "").toLowerCase().includes(normalizedQuery);

  const getArticleByCategory = (category) => {
    const categoryArticles = articles.filter(
      (a) => a.category === category._id
    );

    const categoryNameMatches = matchesQuery(category.name);
    const visibleArticles = categoryNameMatches
      ? categoryArticles
      : categoryArticles.filter((a) => matchesQuery(a.name));

    return {
      totalArticlesCount: categoryArticles.length,
      matchedArticlesCount: visibleArticles.length,
      articles: visibleArticles.slice(0, 5),
      categoryNameMatches,
    };
  };

  const filteredCategories = !normalizedQuery
    ? categories
    : categories.filter((cat) => {
        if (matchesQuery(cat.name)) return true;
        return articles.some(
          (a) => a.category === cat._id && matchesQuery(a.name)
        );
      });

  if (loading) {
    return <ThemedLoader variant="page" label="Loading knowledgebase" />;
  }

  const handleLanguageChange = async (value) => {
    const { response } = await AppService.knowledgebase_customization.create({
      payload: {
        companyId: companyId,
        languageId: value,
      },
    });
    if (response) {
      Update(response.data);
    }
  };

  const handleVisibility = async (data, status) => {
    const { response } =
      await AppService.knowledgebase_category.data_visibility({
        payload: {
          companyId: companyId,
          dataId: data?._id,
          dataType: "CATEGORY",
          status: status,
        },
      });
    if (response) {
      getCategories();
    }
  };

  const headerTextColor = KnowledgebaseCustomization?.textColor || "white";

  return (
    <div className="kb-page">
      {/* Hero */}
      <header
        className="kb-hero text-center"
        style={{ background: KnowledgebaseCustomization?.bgColor || "#6c757d" }}
      >
        <div className="container kb-hero-inner">
          <h2 className="kb-hero-title" style={{ color: headerTextColor }}>
            How can we help you?
          </h2>
          <p className="kb-hero-subtitle" style={{ color: headerTextColor }}>
            Search for answers on orders, returns, and account issues.
          </p>

          <div className="kb-hero-search-wrap mx-auto">
            <span className="kb-hero-search-icon" aria-hidden>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              type="text"
              className="kb-hero-search"
              placeholder="Search categories or articles..."
              aria-label="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="kb-hero-search-clear"
                type="button"
                onClick={() => setSearchQuery("")}
                title="Clear search"
                aria-label="Clear search"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          {normalizedQuery && (
            <div className="kb-hero-result-line" style={{ color: headerTextColor }}>
              {filteredCategories.length === 0
                ? `No matches for "${searchQuery}"`
                : `Showing results for "${searchQuery}"`}
            </div>
          )}
        </div>
      </header>

      {/* Toolbar */}
      <div className="container kb-toolbar d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="kb-lang-select" style={{ minWidth: 200 }}>
            <Select
              options={languages}
              defaultValue={languages.find((item) => languageId == item.value)}
              onChange={(e) => handleLanguageChange(e.value)}
              placeholder="Change language"
              isSearchable
            />
          </div>
          <Link to={`./customization`} className="kb-btn kb-btn--ghost">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            Customization
          </Link>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <CreateCategory
            languageId={languageId}
            handleRefresh={getCategories}
          />
          <Link to={`./create_article`} className="kb-btn kb-btn--primary">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="me-1">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Create article
          </Link>
        </div>
      </div>

      {/* Categories */}
      <div className="container kb-section">
        <h3 className="kb-section-title">
          {normalizedQuery ? "Search results" : "Browse topics"}
        </h3>

        {filteredCategories.length === 0 && (
          <div className="kb-empty d-flex flex-column align-items-center text-center">
            <div className="kb-empty-icon d-flex align-items-center justify-content-center mb-3">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <h5 className="kb-empty-title">No categories or articles found</h5>
            <p className="kb-empty-text mb-0">
              Try a different search term or clear the search to see all topics.
            </p>
          </div>
        )}

        <div className="row g-3">
          {filteredCategories.map((cat) => {
            const { articles, totalArticlesCount, matchedArticlesCount } =
              getArticleByCategory(cat);

            return (
              <div className="col-md-6 col-lg-4" key={cat._id} count={totalArticlesCount}>
                <div className="kb-card h-100 d-flex flex-column">
                  <div className="kb-card-header d-flex align-items-center gap-3">
                    <span className="kb-card-icon d-inline-flex align-items-center justify-content-center">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                      </svg>
                    </span>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <h6 className="kb-card-title mb-0 text-truncate">{cat.name}</h6>
                      <div className="kb-card-meta">
                        {totalArticlesCount}{" "}
                        {totalArticlesCount === 1 ? "article" : "articles"}
                      </div>
                    </div>
                    {cat?.createdBy == "AGENCY" ? (
                      <CreateCategory
                        data={cat}
                        languageId={languageId}
                        handleRefresh={getCategories}
                      />
                    ) : cat?.hidden ? (
                      <button
                        className="kb-icon-btn kb-icon-btn--active"
                        title="Show"
                        onClick={() => handleVisibility(cat, "visible")}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        className="kb-icon-btn"
                        title="Hide"
                        onClick={() => handleVisibility(cat, "hide")}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    )}
                  </div>

                  <div className="kb-card-body flex-grow-1">
                    {articles.length > 0 ? (
                      <ul className={`kb-article-list ${cat.hidden ? "is-disabled" : ""}`}>
                        {articles.map((sub) => (
                          <li
                            key={sub._id}
                            className="kb-article-item d-flex justify-content-between align-items-center"
                            style={{ cursor: cat.hidden ? "not-allowed" : "pointer" }}
                            onClick={(e) => {
                              if (cat.hidden) {
                                e.preventDefault();
                              } else {
                                navigate(`./${cat._id}/${sub._id}`);
                              }
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 0 }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="kb-article-icon flex-shrink-0">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="8" y1="13" x2="16" y2="13" />
                                <line x1="8" y1="17" x2="13" y2="17" />
                              </svg>
                              <span className="kb-article-name text-truncate">{sub.name}</span>
                            </div>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="kb-article-chevron flex-shrink-0">
                              <polyline points="9 18 15 12 9 6" />
                            </svg>
                          </li>
                        ))}
                        {normalizedQuery && matchedArticlesCount > 5 && (
                          <li className="kb-article-more">
                            +{matchedArticlesCount - 5} more matching article
                            {matchedArticlesCount - 5 === 1 ? "" : "s"}
                          </li>
                        )}
                      </ul>
                    ) : (
                      <p className="kb-card-empty mb-0">
                        {normalizedQuery
                          ? "No matching articles in this category"
                          : "No articles available"}
                      </p>
                    )}
                  </div>

                  <div className="kb-card-footer">
                    <div
                      style={{ cursor: cat.hidden ? "not-allowed" : "pointer" }}
                      onClick={(e) => {
                        if (cat.hidden) {
                          e.preventDefault();
                        } else {
                          navigate(`./${cat._id}`);
                        }
                      }}
                      className="kb-btn kb-btn--ghost w-100 justify-content-center"
                    >
                      View all articles
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ms-1">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <KbStyles />
    </div>
  );
};

const KbStyles = () => (
  <style>{`
    .kb-page {
      font-family: 'Inter', sans-serif;
      background: #fff;
      min-height: 100vh;
    }

    /* Hero */
    .kb-hero {
      padding: 56px 16px 64px;
      margin-bottom: 0;
      position: relative;
    }
    .kb-hero-inner {
      max-width: 720px;
    }
    .kb-hero-title {
      font-size: 2rem;
      font-weight: 700;
      letter-spacing: -0.01em;
      margin: 0 0 10px;
    }
    .kb-hero-subtitle {
      font-size: 1rem;
      opacity: 0.92;
      margin: 0 0 28px;
    }
    .kb-hero-search-wrap {
      position: relative;
      max-width: 560px;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.06);
    }
    .kb-hero-search-icon {
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
      color: #9ca3af;
      pointer-events: none;
      display: inline-flex;
    }
    .kb-hero-search {
      width: 100%;
      height: 48px;
      border: 0;
      border-radius: 12px;
      padding: 0 48px 0 44px;
      background: #fff;
      color: #1f2937;
      font-size: 0.9375rem;
      outline: none;
      font-family: inherit;
    }
    .kb-hero-search::placeholder { color: #9ca3af; }
    .kb-hero-search-clear {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 30px;
      height: 30px;
      border: 0;
      background: transparent;
      color: #6b7280;
      border-radius: 8px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .kb-hero-search-clear:hover { background: #f3f4f6; color: #1f2937; }
    .kb-hero-result-line {
      margin-top: 14px;
      font-size: 0.8125rem;
      opacity: 0.92;
    }

    /* Toolbar */
    .kb-toolbar {
      margin-top: 28px;
    }

    /* Buttons */
    .kb-btn {
      display: inline-flex;
      align-items: center;
      font-size: 0.8125rem;
      font-weight: 600;
      letter-spacing: 0.005em;
      padding: 8px 14px;
      border-radius: 8px;
      border: 1px solid transparent;
      line-height: 1;
      cursor: pointer;
      font-family: inherit;
      text-decoration: none;
      transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
    }
    .kb-btn:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
    }
    .kb-btn--ghost {
      background: #fff;
      border-color: #e5e7eb;
      color: #1f2937;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
    }
    .kb-btn--ghost:hover {
      background: #fafafa;
      border-color: #d1d5db;
      color: #1f2937;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 6px 14px rgba(17, 24, 39, 0.06);
      text-decoration: none;
    }
    .kb-btn--ghost:active { transform: translateY(0); }
    .kb-btn--primary {
      background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
      color: #fff;
      border-color: rgba(255, 255, 255, 0.04);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 1px 2px rgba(79, 70, 229, 0.22),
        0 4px 10px rgba(79, 70, 229, 0.2);
    }
    .kb-btn--primary:hover {
      background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
      color: #fff;
      transform: translateY(-1px);
      text-decoration: none;
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 2px 4px rgba(67, 56, 202, 0.28),
        0 8px 18px rgba(79, 70, 229, 0.3);
    }
    .kb-btn--primary:active { transform: translateY(0); }

    .kb-icon-btn {
      width: 30px;
      height: 30px;
      border: 1px solid #e5e7eb;
      background: #fff;
      color: #4b5563;
      border-radius: 7px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
      transition: transform 0.15s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.2s ease;
    }
    .kb-icon-btn:hover {
      background: #fafafa;
      border-color: #d1d5db;
      color: #1f2937;
      transform: translateY(-1px);
    }
    .kb-icon-btn:active { transform: translateY(0); }
    .kb-icon-btn--active {
      background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
      border-color: rgba(255, 255, 255, 0.04);
      color: #fff;
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 1px 2px rgba(79, 70, 229, 0.22);
    }
    .kb-icon-btn--active:hover {
      background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
      color: #fff;
    }

    /* Section */
    .kb-section { padding: 32px 16px 64px; }
    .kb-section-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
      letter-spacing: -0.01em;
      margin: 0 0 24px;
      text-align: center;
    }

    /* Card */
    .kb-card {
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 12px;
      overflow: hidden;
      transition: border-color 0.15s ease, box-shadow 0.2s ease;
    }
    .kb-card:hover {
      border-color: #dcdcdc;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04), 0 8px 20px rgba(17, 24, 39, 0.06);
    }
    .kb-card-header {
      padding: 14px 16px;
      border-bottom: 1px solid #f1f1f1;
      background: #fafafa;
    }
    .kb-card-icon {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      background: #eef2ff;
      color: #4f46e5;
      flex-shrink: 0;
    }
    .kb-card-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      letter-spacing: -0.005em;
    }
    .kb-card-meta {
      font-size: 0.75rem;
      color: #6b7280;
      margin-top: 2px;
    }
    .kb-card-body { padding: 14px 16px 6px; }
    .kb-card-footer { padding: 8px 16px 14px; }
    .kb-card-empty {
      font-size: 0.8125rem;
      color: #9ca3af;
      padding: 6px 0 10px;
    }

    /* Article list inside card */
    .kb-article-list {
      list-style: none;
      margin: 0;
      padding: 0;
    }
    .kb-article-list.is-disabled { opacity: 0.5; }
    .kb-article-item {
      padding: 9px 10px;
      border-radius: 7px;
      transition: background 0.12s ease, color 0.12s ease;
      font-size: 0.875rem;
      color: #1f2937;
    }
    .kb-article-item + .kb-article-item { margin-top: 2px; }
    .kb-article-item:hover {
      background: #f5f3ff;
      color: #4338ca;
    }
    .kb-article-icon {
      color: #9ca3af;
      transition: color 0.12s ease;
    }
    .kb-article-item:hover .kb-article-icon { color: #4f46e5; }
    .kb-article-chevron {
      color: #cbd5e1;
      transition: transform 0.12s ease, color 0.12s ease;
    }
    .kb-article-item:hover .kb-article-chevron {
      color: #4f46e5;
      transform: translateX(2px);
    }
    .kb-article-name {
      font-weight: 500;
    }
    .kb-article-more {
      font-size: 0.75rem;
      color: #6b7280;
      padding: 6px 10px;
    }

    /* Empty */
    .kb-empty {
      border: 1px dashed #e5e5e5;
      border-radius: 12px;
      background: #fafafa;
      padding: 40px 16px;
      margin-bottom: 24px;
    }
    .kb-empty-icon {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: #fff;
      border: 1px solid #ececec;
      color: #9ca3af;
    }
    .kb-empty-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      margin: 0 0 4px;
    }
    .kb-empty-text {
      font-size: 0.8125rem;
      color: #6b7280;
    }
  `}</style>
);

export default HelpCenter;
